// The pitch-preserving stretch: a phase vocoder with identity phase locking
// and phase resets on transients. It replaces the WSOLA stretch: WSOLA was fine
// on drums but warbled on anything tonal.
//
// Each output frame k sits at a fixed synthesis hop. It is analysed at the
// input position the time map puts under its centre, and again one hop
// earlier. The phase difference between those two frames gives each bin's
// true frequency, and the frame's phases advance by that frequency over one
// hop. So any map works: a constant ratio, a warp-marker map, a segment
// that speeds up next to one that slows down. The frame sizes below are the
// ones the implementation actually uses.
//
// - Phase locking (Laroche–Dolson "identity"): only spectral peaks advance
//   freely. Each other bin keeps its analysed phase offset from the peak
//   that owns it, so a partial's sidebands stay coherent. This removes most
//   of a plain vocoder's phasiness.
// - Transients: a frame whose centre crosses a strong onset takes the
//   analysed phases as they are (a phase reset). A 46 ms frame still leaks
//   a hit's energy up to half a frame early (pre-echo), so each strong
//   onset also gets its ORIGINAL attack spliced back in, unstretched,
//   anchored where the map puts that onset. The hit lands on its beat,
//   sample-exact and as sharp as the file. The splice crossfades (equal
//   power) into the vocoder either side and never reaches halfway to the
//   next hit. A WEAK onset (too weak to splice) resets only the peaks that
//   jumped (+6 dB over one hop): the sustained notes under a soft hit keep
//   advancing instead of being re-phased — a full reset there made a
//   steady vowel flutter by ~1 dB a frame (stretch-rs/README.md).
// - Stereo: phases advance on the mid (L+R) spectrum, and each channel keeps
//   its analysed phase offset from mid. The image stays where it was instead
//   of drifting.

import type { PcmSource } from "@web-shared/lib/wav";
import type { StretchedPcm } from "@web-shared/lib/warp";
import { fftOf, hannWindow } from "@web-shared/lib/stretch/fft";
import { detectOnsets, type Onset } from "@web-shared/lib/stretch/onsets";

/** ~46 ms analysis frames (2048 at 44.1 kHz), 4× overlap. */
function frameFor(sampleRate: number): number {
  return Math.max(256, 2 ** Math.round(Math.log2(sampleRate * 0.0464)));
}

const TWO_PI = 2 * Math.PI;

function princarg(p: number): number {
  return p - TWO_PI * Math.round(p / TWO_PI);
}

export interface VocodeOptions {
  /** The file's transients (`detectOnsets`); detected when absent. */
  onsets?: readonly Onset[];
}

/** An onset at least this strong gets its attack spliced back in (and a
 * full phase reset); a weaker one resets only the peaks that jumped. */
const SPLICE_STRENGTH = 0.25;
/** A peak this much louder than one hop earlier has just started. */
const RISE = 2;
const SPLICE_PRE_SEC = 0.02;
const SPLICE_POST_SEC = 0.04;
const FADE_IN_SEC = 0.004;
const FADE_OUT_SEC = 0.01;

/**
 * `outLen` output samples of `source`: output sample o carries the sound at
 * input sample `mapIn(o)`, at the pitch it has there. The map must not run
 * backwards.
 */
export function vocode(
  source: PcmSource,
  outLen: number,
  mapIn: (outSample: number) => number,
  opts: VocodeOptions = {},
): StretchedPcm {
  const sr = source.sampleRate;
  const channels = Math.max(1, source.numberOfChannels);
  const inputs = Array.from({ length: channels }, (_, c) => source.getChannelData(Math.min(c, source.numberOfChannels - 1)));
  const inLen = source.length;
  const outs = inputs.map(() => new Float32Array(new ArrayBuffer(Math.max(0, outLen) * 4)));
  if (outLen <= 0 || inLen === 0) return pcmOf(sr, outs);

  const n = frameFor(sr);
  const hop = n >> 2;
  const half = n >> 1;
  const bins = half + 1;
  const fft = fftOf(n);
  const win = hannWindow(n);
  const norm = new Float64Array(outLen);
  const found = [...(opts.onsets ?? detectOnsets(source))].sort((a, b) => a.sample - b.sample);
  const onsets = found.map((o) => o.sample);
  const strong = found.map((o) => o.strength >= SPLICE_STRENGTH);
  const stereo = channels > 1;

  // Per-channel scratch: current and previous-hop spectra.
  const reCur = inputs.map(() => new Float64Array(n));
  const imCur = inputs.map(() => new Float64Array(n));
  const rePrev = inputs.map(() => new Float64Array(n));
  const imPrev = inputs.map(() => new Float64Array(n));
  // Reference (mid) spectrum analysis.
  const magRef = new Float64Array(bins);
  const magRefPrev = new Float64Array(bins);
  const phRef = new Float64Array(bins);
  const phRefPrev = new Float64Array(bins);
  // Synthesis phase of the reference, carried frame to frame.
  const synth = new Float64Array(bins);
  const outPh = new Float64Array(bins);
  const owner = new Int32Array(bins);
  const peaks: number[] = [];
  const oRe = new Float64Array(n);
  const oIm = new Float64Array(n);

  const analyse = (at: number, re: Float64Array, im: Float64Array, src: Float32Array): void => {
    const start = Math.round(at);
    for (let i = 0; i < n; i++) {
      const s = start + i;
      re[i] = s >= 0 && s < inLen ? src[s]! * win[i]! : 0;
      im[i] = 0;
    }
    fft.forward(re, im);
  };

  let prevCentre = Number.NEGATIVE_INFINITY;
  let onsetAt = 0;
  let first = true;
  const frames = Math.ceil((outLen + half) / hop) + 1;
  for (let k = 0; k < frames; k++) {
    const outStart = k * hop - half; // synthesis frame k is centred on output sample k·hop
    const centre = mapIn(Math.max(0, Math.min(outLen, k * hop)));
    const at = centre - half;
    for (let c = 0; c < channels; c++) {
      analyse(at, reCur[c]!, imCur[c]!, inputs[c]!);
      analyse(at - hop, rePrev[c]!, imPrev[c]!, inputs[c]!);
    }
    // Reference spectrum: mid for stereo, the one channel otherwise.
    for (let b = 0; b < bins; b++) {
      let r = 0;
      let i = 0;
      let rp = 0;
      let ip = 0;
      for (let c = 0; c < channels; c++) {
        r += reCur[c]![b]!;
        i += imCur[c]![b]!;
        rp += rePrev[c]![b]!;
        ip += imPrev[c]![b]!;
      }
      magRef[b] = Math.hypot(r, i);
      magRefPrev[b] = Math.hypot(rp, ip);
      phRef[b] = Math.atan2(i, r);
      phRefPrev[b] = Math.atan2(ip, rp);
    }

    // A transient between the last frame's centre and this one: a full
    // reset when it is strong (its attack gets spliced in, so the frame
    // must be coherent with the original); a weak one only resets the
    // peaks that jumped, below.
    let reset = first;
    let hit = false;
    while (onsetAt < onsets.length && onsets[onsetAt]! <= centre) {
      if (onsets[onsetAt]! > prevCentre) {
        hit = true;
        if (strong[onsetAt]) reset = true;
      }
      onsetAt++;
    }
    // A backwards or stalled map has no meaningful frequency estimate.
    if (centre <= prevCentre - hop) reset = true;
    prevCentre = centre;
    first = false;

    if (reset) {
      for (let b = 0; b < bins; b++) synth[b] = phRef[b]!;
    } else {
      // Peaks own the bins nearest them; each peak's phase advances by its
      // measured frequency, and the bins it owns keep their offset from it.
      peaks.length = 0;
      for (let b = 2; b < bins - 2; b++) {
        const m = magRef[b]!;
        if (m > magRef[b - 1]! && m >= magRef[b + 1]! && m > magRef[b - 2]! && m >= magRef[b + 2]!) peaks.push(b);
      }
      if (peaks.length === 0) peaks.push(0);
      let p = 0;
      for (let b = 0; b < bins; b++) {
        while (p + 1 < peaks.length && Math.abs(peaks[p + 1]! - b) < Math.abs(peaks[p]! - b)) p++;
        owner[b] = peaks[p]!;
      }
      for (const pk of peaks) {
        if (hit && magRef[pk]! > RISE * magRefPrev[pk]!) {
          outPh[pk] = phRef[pk]!;
          continue;
        }
        const expected = (TWO_PI * pk * hop) / n;
        const dev = princarg(phRef[pk]! - phRefPrev[pk]! - expected);
        outPh[pk] = synth[pk]! + expected + dev;
      }
      for (let b = 0; b < bins; b++) {
        const pk = owner[b]!;
        if (pk !== b) outPh[b] = outPh[pk]! + (phRef[b]! - phRef[pk]!);
      }
      for (let b = 0; b < bins; b++) synth[b] = princarg(outPh[b]!);
    }

    // Each channel: its own magnitude, the reference's synthesis phase plus
    // the channel's analysed offset from the reference.
    for (let c = 0; c < channels; c++) {
      const re = reCur[c]!;
      const im = imCur[c]!;
      for (let b = 0; b < bins; b++) {
        const mag = Math.hypot(re[b]!, im[b]!);
        const ph = stereo ? synth[b]! + (Math.atan2(im[b]!, re[b]!) - phRef[b]!) : synth[b]!;
        oRe[b] = mag * Math.cos(ph);
        oIm[b] = mag * Math.sin(ph);
      }
      oIm[0] = 0;
      oIm[half] = 0;
      for (let b = 1; b < half; b++) {
        oRe[n - b] = oRe[b]!;
        oIm[n - b] = -oIm[b]!;
      }
      fft.inverse(oRe, oIm);
      const dst = outs[c]!;
      for (let i = 0; i < n; i++) {
        const o = outStart + i;
        if (o < 0 || o >= outLen) continue;
        dst[o] = dst[o]! + oRe[i]! * win[i]!;
        if (c === 0) norm[o] = norm[o]! + win[i]! * win[i]!;
      }
    }
  }
  for (const dst of outs) {
    for (let i = 0; i < outLen; i++) {
      const w = norm[i]!;
      if (w > 1e-6) dst[i] = dst[i]! / w;
    }
  }
  spliceAttacks(inputs, outs, outLen, mapIn, found, sr);
  return pcmOf(sr, outs);
}

/** The output sample the map puts file sample `target` at (the map is monotonic). */
function outputAt(mapIn: (o: number) => number, outLen: number, target: number): number | null {
  if (mapIn(0) > target || mapIn(outLen) < target) return null;
  let lo = 0;
  let hi = outLen;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (mapIn(mid) < target) lo = mid;
    else hi = mid;
  }
  return hi;
}

/**
 * Each strong onset's original attack, copied over the vocoder's output at
 * the place the map puts it: [pre, post] around the onset, equal-power
 * crossfades at both ends, never reaching past 40% of the way to the
 * neighbouring spliced onset (so dense hits never overlap).
 */
function spliceAttacks(
  inputs: Float32Array[],
  outs: Float32Array[],
  outLen: number,
  mapIn: (o: number) => number,
  onsets: readonly Onset[],
  sr: number,
): void {
  const inLen = inputs[0]?.length ?? 0;
  const placed: Array<{ inAt: number; outAt: number }> = [];
  for (const o of onsets) {
    if (o.strength < SPLICE_STRENGTH) continue;
    const outAt = outputAt(mapIn, outLen, o.sample);
    if (outAt !== null) placed.push({ inAt: o.sample, outAt });
  }
  for (let i = 0; i < placed.length; i++) {
    const { inAt, outAt } = placed[i]!;
    const prevGap = i > 0 ? outAt - placed[i - 1]!.outAt : Number.POSITIVE_INFINITY;
    const nextGap = i + 1 < placed.length ? placed[i + 1]!.outAt - outAt : Number.POSITIVE_INFINITY;
    const pre = Math.floor(Math.min(SPLICE_PRE_SEC * sr, 0.4 * prevGap));
    const post = Math.floor(Math.min(SPLICE_POST_SEC * sr, 0.4 * nextGap));
    if (post < 8) continue;
    const fadeIn = Math.max(1, Math.min(Math.round(FADE_IN_SEC * sr), pre));
    const fadeOut = Math.max(1, Math.min(Math.round(FADE_OUT_SEC * sr), post >> 1));
    const from = outAt - pre;
    const to = outAt + post;
    for (let o = Math.max(0, from); o < Math.min(outLen, to); o++) {
      const g = o - from < fadeIn ? (o - from) / fadeIn : to - o <= fadeOut ? (to - o) / fadeOut : 1;
      const wPv = Math.cos((g * Math.PI) / 2);
      const wIn = Math.sin((g * Math.PI) / 2);
      const s = inAt + (o - outAt);
      for (let c = 0; c < outs.length; c++) {
        const orig = s >= 0 && s < inLen ? inputs[c]![s]! : 0;
        outs[c]![o] = outs[c]![o]! * wPv + orig * wIn;
      }
    }
  }
}

function pcmOf(sampleRate: number, channels: Float32Array<ArrayBuffer>[]): StretchedPcm {
  return {
    sampleRate,
    numberOfChannels: channels.length,
    length: channels[0]?.length ?? 0,
    channels,
    getChannelData: (c) => channels[c] ?? channels[0]!,
  };
}
